import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, Table, Button, Modal, Form, Spinner, Badge, Row, Col, InputGroup, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axios from 'axios';
import HelpIcon from './HelpIcon';
import { getHelpContent } from './helpContent';
import { useAuth } from '../contexts/AuthContext';
import {
  countries,
  getStatesForCountry,
  getCurrencyForCountry,
  commonCurrencyCodes,
  getOrgAddressDefaults
} from '../utils/countriesStates';
import SearchableSelect from './SearchableSelect';
import OrgPrintChrome from './OrgPrintChrome';
import { todayISO } from '../utils/dates';

import DateInput from './DateInput';
import { formatDate } from '../utils/dateFormat';
const emptyForm = {
  surveyNumber: '',
  surveyDate: todayISO(),
  surveyorId: '',
  vendorId: '',
  htVendorId: '',
  itemId: '',
  siteName: '',
  address: '',
  city: '',
  district: '',
  state: '',
  country: '',
  postalCode: '',
  areaAcres: '',
  estimatedQuantity: '',
  unitOfMeasurementId: '',
  bindingDeductions: '',
  price: '',
  priceCurrency: '',
  remarks: ''
};

function statusBadge(status) {
  const map = {
    NEW: 'secondary',
    IN_PROGRESS: 'info',
    COMPLETED: 'success',
    CANCELLED: 'danger'
  };
  const labels = {
    NEW: 'New',
    IN_PROGRESS: 'In Progress',
    COMPLETED: 'Completed',
    CANCELLED: 'Cancelled'
  };
  return <Badge bg={map[status] || 'secondary'}>{labels[status] || status}</Badge>;
}

function formatSiteLocation(s) {
  const parts = [s.siteName, s.city, s.state, s.country].filter(Boolean);
  if (parts.length) return parts.join(', ');
  return s.address || '-';
}

const ICON_EDIT = (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
    <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/>
    <path fillRule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z"/>
  </svg>
);

const ICON_DELETE = (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
    <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
    <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
  </svg>
);

const ICON_COMPLETE = (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
    <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
    <path d="M10.97 4.97a.75.75 0 0 1 1.07 1.05l-3.99 4.99a.75.75 0 0 1-1.08.02L4.324 8.384a.75.75 0 1 1 1.06-1.06l2.094 2.093 3.473-4.425a.267.267 0 0 1 .02-.022z"/>
  </svg>
);

const ICON_CANCEL = (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
    <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
    <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"/>
  </svg>
);

const ICON_PRINT = (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
    <path d="M5 1a2 2 0 0 0-2 2v1h10V3a2 2 0 0 0-2-2H5zm6 8H5a1 1 0 0 0-1 1v3a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-3a1 1 0 0 0-1-1z"/>
    <path d="M0 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-1v-2a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v2H2a2 2 0 0 1-2-2V7zm2.5 1a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1z"/>
  </svg>
);

const ICON_TRANSACTIONS = (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
    <path d="M1 11.5a.5.5 0 0 0 .5.5h11.793l-3.147 3.146a.5.5 0 0 0 .708.708l4-4a.5.5 0 0 0 0-.708l-4-4a.5.5 0 0 0-.708.708L13.293 11H1.5a.5.5 0 0 0-.5.5zm14-7a.5.5 0 0 1-.5.5H2.707l3.147 3.146a.5.5 0 1 1-.708.708l-4-4a.5.5 0 0 1 0-.708l4-4a.5.5 0 1 1 .708.708L2.707 4H14.5a.5.5 0 0 1 .5.5z"/>
  </svg>
);

/**
 * Procurement Survey (Field Slip) — common fields + vendor site address + workflow.
 */
function ProcurementSurveysTab({ setHelpContent, setShowHelpPane }) {
  const { user } = useAuth();
  const [surveys, setSurveys] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [attrDefsByTypeId, setAttrDefsByTypeId] = useState({});
  const [materialCatalogDetails, setMaterialCatalogDetails] = useState(null);
  const [loadingMaterialCatalog, setLoadingMaterialCatalog] = useState(false);
  const [users, setUsers] = useState([]);
  const [uoms, setUoms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [statusAction, setStatusAction] = useState(null); // { id, type: 'complete'|'cancel', surveyNumber }
  const [statusComments, setStatusComments] = useState('');
  const [statusActionSaving, setStatusActionSaving] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printData, setPrintData] = useState(null);
  const [showTransactionsModal, setShowTransactionsModal] = useState(false);
  const [selectedSurveyForTransactions, setSelectedSurveyForTransactions] = useState(null);
  const [loadingSurveyTransactions, setLoadingSurveyTransactions] = useState(false);
  const [surveyTransactions, setSurveyTransactions] = useState({ deliveries: [], weighBridgeReceipts: [] });
  const [orgCurrency, setOrgCurrency] = useState('');
  const [orgCountry, setOrgCountry] = useState('');
  const [orgState, setOrgState] = useState('');
  const [selectedCountryCode, setSelectedCountryCode] = useState('');

  const fetchSurveys = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/inventory/procurement-surveys');
      setSurveys(response.data || []);
    } catch (error) {
      toast.error('Error fetching procurement surveys');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLookups = useCallback(async () => {
    try {
      const [vendorsRes, materialsRes, usersRes, uomsRes] = await Promise.all([
        axios.get('/api/inventory/vendors/active'),
        axios.get('/api/organization-materials'),
        axios.get('/api/users'),
        axios.get('/api/inventory/units-of-measurement')
      ]);
      const mats = materialsRes.data || [];
      setVendors(vendorsRes.data || []);
      setMaterials(mats);
      setUsers(usersRes.data || []);
      setUoms(uomsRes.data || []);

      const typeIds = [
        ...new Set(
          mats
            .map((m) => m.item?.itemType?.id)
            .filter((id) => id != null)
            .map(String)
        )
      ];
      if (typeIds.length > 0) {
        const defsEntries = await Promise.all(
          typeIds.map(async (typeId) => {
            try {
              const res = await axios.get(`/api/inventory/item-types/${typeId}/attributes`);
              return [typeId, res.data || []];
            } catch (_) {
              return [typeId, []];
            }
          })
        );
        setAttrDefsByTypeId(Object.fromEntries(defsEntries));
      } else {
        setAttrDefsByTypeId({});
      }

      if (user?.organizationId) {
        try {
          const orgRes = await axios.get(`/api/organizations/${user.organizationId}`);
          setOrgCurrency(orgRes.data?.defaultCurrency || '');
          setOrgCountry(orgRes.data?.country || '');
          setOrgState(orgRes.data?.state || '');
        } catch (_) {
          // optional
        }
      }
    } catch (error) {
      console.error(error);
    }
  }, [user?.organizationId]);

  useEffect(() => {
    fetchSurveys();
    loadLookups();
  }, [fetchSurveys, loadLookups]);

  const defaultKgUomId = () => {
    const kg = uoms.find(u => {
      const symbol = (u.symbol || '').toLowerCase();
      const name = (u.name || '').toLowerCase();
      return symbol === 'kg' || name === 'kg' || name === 'kilogram' || name === 'kilograms';
    });
    return kg ? String(kg.id) : '';
  };

  const currencyMismatch = useMemo(() => {
    const orgExpected = getCurrencyForCountry(orgCountry);
    const siteExpected = getCurrencyForCountry(formData.country || selectedCountryCode);
    if (!orgCurrency) return Boolean(orgExpected || siteExpected);
    if (orgExpected && orgExpected !== orgCurrency) return true;
    if (siteExpected && siteExpected !== orgCurrency) return true;
    return false;
  }, [orgCountry, orgCurrency, formData.country, selectedCountryCode]);

  const displayCurrency = currencyMismatch
    ? (formData.priceCurrency || orgCurrency || '')
    : (orgCurrency || '');

  const defaultSurveyorId = () => (user?.id != null ? String(user.id) : '');

  const applyOrgAddressDefaults = (form, country = orgCountry, state = orgState) => {
    const defaults = getOrgAddressDefaults({ country, state });
    setSelectedCountryCode(defaults.countryCode);
    return {
      ...form,
      country: defaults.country,
      state: defaults.state
    };
  };

  const resetForm = (country = orgCountry, state = orgState) => {
    setFormData(applyOrgAddressDefaults({
      ...emptyForm,
      surveyDate: todayISO(),
      surveyorId: defaultSurveyorId(),
      unitOfMeasurementId: defaultKgUomId(),
      priceCurrency: orgCurrency || ''
    }, country, state));
    setMaterialCatalogDetails(null);
    setEditing(null);
  };

  const handleCreate = async () => {
    let country = orgCountry;
    let state = orgState;
    let currency = orgCurrency;
    if ((!country && !state) && user?.organizationId) {
      try {
        const orgRes = await axios.get(`/api/organizations/${user.organizationId}`);
        country = orgRes.data?.country || '';
        state = orgRes.data?.state || '';
        currency = orgRes.data?.defaultCurrency || currency;
        setOrgCountry(country);
        setOrgState(state);
        setOrgCurrency(currency);
      } catch (_) {
        // optional
      }
    }
    setFormData(applyOrgAddressDefaults({
      ...emptyForm,
      surveyDate: todayISO(),
      surveyorId: defaultSurveyorId(),
      unitOfMeasurementId: defaultKgUomId(),
      priceCurrency: currency || ''
    }, country, state));
    setMaterialCatalogDetails(null);
    setEditing(null);
    setShowModal(true);
  };

  const handleEdit = (survey) => {
    setEditing(survey);
    const countryName = survey.country || '';
    const countryObj = countries.find(c => c.name === countryName);
    setSelectedCountryCode(countryObj ? countryObj.code : (countryName ? 'OTHER' : ''));
    const attrs = survey.attributes || {};
    setFormData({
      surveyNumber: survey.surveyNumber || '',
      surveyDate: survey.surveyDate || '',
      surveyorId: survey.surveyor?.id || '',
      vendorId: survey.vendor?.id || '',
      htVendorId: survey.htVendor?.id || '',
      itemId: survey.item?.id || '',
      siteName: survey.siteName || '',
      address: survey.address || '',
      city: survey.city || '',
      district: survey.district || '',
      state: survey.state || '',
      country: countryName,
      postalCode: survey.postalCode || '',
      areaAcres: survey.areaAcres != null ? String(survey.areaAcres) : '',
      estimatedQuantity: survey.estimatedQuantity != null ? String(survey.estimatedQuantity) : '',
      unitOfMeasurementId: survey.unitOfMeasurement?.id || '',
      bindingDeductions: survey.bindingDeductions != null ? String(survey.bindingDeductions) : '',
      price: survey.price != null ? String(survey.price) : '',
      priceCurrency: attrs.priceCurrency || orgCurrency || '',
      remarks: survey.remarks || ''
    });
    setShowModal(true);
    if (survey.item?.id) {
      fetchMaterialCatalogDetails(survey.item.id);
    } else {
      setMaterialCatalogDetails(null);
    }
  };

  const buildPayload = () => {
    const attributes = {};
    if (currencyMismatch && formData.priceCurrency) {
      attributes.priceCurrency = formData.priceCurrency;
    }
    return {
      surveyNumber: formData.surveyNumber || null,
      surveyDate: formData.surveyDate,
      surveyor: formData.surveyorId ? { id: parseInt(formData.surveyorId, 10) } : null,
      vendor: { id: parseInt(formData.vendorId, 10) },
      htVendor: formData.htVendorId ? { id: parseInt(formData.htVendorId, 10) } : null,
      item: { id: parseInt(formData.itemId, 10) },
      siteName: formData.siteName || null,
      address: formData.address || null,
      city: formData.city || null,
      district: formData.district || null,
      state: formData.state || null,
      country: formData.country || null,
      postalCode: formData.postalCode || null,
      areaAcres: formData.areaAcres !== '' ? parseFloat(formData.areaAcres) : null,
      estimatedQuantity: formData.estimatedQuantity !== '' ? parseFloat(formData.estimatedQuantity) : null,
      unitOfMeasurement: formData.unitOfMeasurementId
        ? { id: parseInt(formData.unitOfMeasurementId, 10) }
        : null,
      bindingDeductions: formData.bindingDeductions !== '' ? parseFloat(formData.bindingDeductions) : null,
      price: formData.price !== '' ? parseFloat(formData.price) : null,
      remarks: formData.remarks || null,
      attributes
    };
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.vendorId) {
      toast.error('Supply vendor is required');
      return;
    }
    if (!formData.itemId) {
      toast.error('Material is required');
      return;
    }
    if (currencyMismatch && !formData.priceCurrency) {
      toast.error('Please select a currency for the price/rate');
      return;
    }
    try {
      const payload = buildPayload();
      if (editing) {
        await axios.put(`/api/inventory/procurement-surveys/${editing.id}`, payload);
        toast.success('Survey updated');
      } else {
        await axios.post('/api/inventory/procurement-surveys', payload);
        toast.success('Survey created');
      }
      setShowModal(false);
      resetForm();
      fetchSurveys();
    } catch (error) {
      toast.error(typeof error.response?.data === 'string' ? error.response.data : 'Error saving survey');
    }
  };

  const openStatusAction = (survey, type) => {
    setStatusAction({ id: survey.id, type, surveyNumber: survey.surveyNumber });
    setStatusComments('');
  };

  const closeStatusAction = () => {
    setStatusAction(null);
    setStatusComments('');
    setStatusActionSaving(false);
  };

  const confirmStatusAction = async () => {
    if (!statusAction) return;
    const isCancel = statusAction.type === 'cancel';
    if (isCancel && !statusComments.trim()) {
      toast.error('Comments are required when cancelling a survey');
      return;
    }
    try {
      setStatusActionSaving(true);
      const path = isCancel ? 'cancel' : 'complete';
      await axios.post(`/api/inventory/procurement-surveys/${statusAction.id}/${path}`, {
        comments: statusComments.trim() || null
      });
      toast.success(isCancel ? 'Survey cancelled' : 'Survey marked completed');
      closeStatusAction();
      fetchSurveys();
    } catch (error) {
      toast.error(typeof error.response?.data === 'string' ? error.response.data : 'Error updating survey status');
      setStatusActionSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this survey?')) return;
    try {
      await axios.delete(`/api/inventory/procurement-surveys/${id}`);
      toast.success('Survey deleted');
      fetchSurveys();
    } catch (error) {
      toast.error(typeof error.response?.data === 'string' ? error.response.data : 'Error deleting survey');
    }
  };

  const userLabel = (u) => `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email;

  const vendorOptions = useMemo(
    () => vendors.map((v) => ({
      value: String(v.id),
      label: v.name,
      keywords: [v.companyName, v.email, v.phoneNumber, v.city].filter(Boolean).join(' ')
    })),
    [vendors]
  );

  const formatItemAttributeSubtitle = (item) => {
    if (!item) return '';
    const typeId = item.itemType?.id != null ? String(item.itemType.id) : '';
    const defs = attrDefsByTypeId[typeId] || [];
    const attrs = item.attributes || {};
    return defs
      .map((def) => {
        const raw = attrs[def.code];
        if (raw === null || raw === undefined || raw === '') return null;
        let display;
        if (def.dataType === 'BOOLEAN') {
          display = raw === true || String(raw).toLowerCase() === 'true' ? 'Yes' : 'No';
        } else if (def.unit) {
          display = `${raw} ${def.unit}`;
        } else {
          display = String(raw);
        }
        return `${def.name}: ${display}`;
      })
      .filter(Boolean)
      .join(' · ');
  };

  const fetchMaterialCatalogDetails = async (itemId) => {
    if (!itemId) {
      setMaterialCatalogDetails(null);
      return;
    }
    try {
      setLoadingMaterialCatalog(true);
      const response = await axios.get(`/api/inventory/items/${itemId}/catalog-details`);
      setMaterialCatalogDetails(response.data || null);
    } catch (error) {
      console.error(error);
      setMaterialCatalogDetails(null);
    } finally {
      setLoadingMaterialCatalog(false);
    }
  };

  const materialOptions = useMemo(
    () => materials
      .filter((m) => m.item?.id)
      .map((m) => {
        const subtitle = formatItemAttributeSubtitle(m.item);
        const desc = m.item.description ? String(m.item.description).slice(0, 120) : '';
        return {
          value: String(m.item.id),
          label: m.sku ? `${m.item.name} (${m.sku})` : m.item.name,
          subtitle: [subtitle, desc].filter(Boolean).join(' — ') || undefined,
          keywords: [
            m.item.description,
            m.sku,
            m.item.itemType?.name,
            subtitle
          ].filter(Boolean).join(' '),
          uomId: m.unitOfMeasurement?.id ? String(m.unitOfMeasurement.id) : ''
        };
      }),
    [materials, attrDefsByTypeId]
  );

  const handleMaterialChange = (itemId) => {
    const selected = materialOptions.find((o) => o.value === String(itemId));
    setFormData((prev) => ({
      ...prev,
      itemId,
      unitOfMeasurementId: selected?.uomId || prev.unitOfMeasurementId || defaultKgUomId()
    }));
    fetchMaterialCatalogDetails(itemId);
  };

  const priceCurrencyLabel = (survey) =>
    (survey.attributes && survey.attributes.priceCurrency) || orgCurrency || '';

  const handleViewPrint = (survey) => {
    setPrintData(survey);
    setShowPrintModal(true);
  };

  const handlePrint = () => {
    window.print();
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) {
      return `${orgCurrency || ''} 0.00`.trim();
    }
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (Number.isNaN(numAmount)) return '-';
    return `${orgCurrency || ''} ${numAmount.toFixed(2)}`.trim();
  };

  const formatWeight = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    return parseFloat(value).toLocaleString('en-US', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3
    });
  };

  const handleViewTransactions = async (survey) => {
    setSelectedSurveyForTransactions(survey);
    setShowTransactionsModal(true);
    setLoadingSurveyTransactions(true);
    setSurveyTransactions({ deliveries: [], weighBridgeReceipts: [] });
    try {
      const response = await axios.get(`/api/inventory/procurement-surveys/${survey.id}/transactions`);
      setSurveyTransactions(response.data || { deliveries: [], weighBridgeReceipts: [] });
    } catch (error) {
      toast.error(error.response?.data || 'Failed to load survey transactions');
      setSurveyTransactions({ deliveries: [], weighBridgeReceipts: [] });
    } finally {
      setLoadingSurveyTransactions(false);
    }
  };

  const closeTransactionsModal = () => {
    setShowTransactionsModal(false);
    setSelectedSurveyForTransactions(null);
    setSurveyTransactions({ deliveries: [], weighBridgeReceipts: [] });
  };

  const formatSurveyAttributeEntries = (survey) => {
    const attrs = survey?.attributes || {};
    return Object.entries(attrs)
      .filter(([key, value]) => key !== 'priceCurrency' && value !== null && value !== undefined && value !== '')
      .map(([key, value]) => ({
        key,
        label: key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase()),
        value: typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)
      }));
  };

  const formatSiteAddressBlock = (s) => {
    const lines = [];
    if (s.siteName) lines.push(s.siteName);
    if (s.address) lines.push(s.address);
    const cityLine = [s.city, s.district, s.state, s.postalCode].filter(Boolean).join(', ');
    if (cityLine) lines.push(cityLine);
    if (s.country) lines.push(s.country);
    return lines;
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <p className="text-muted mb-0">
          Field slips / procurement surveys — draft, submit, approve, then use on GRN without a PO
          <HelpIcon
            onClick={(e) => {
              e.stopPropagation();
              const content = getHelpContent('procurement-surveys');
              if (content) {
                setHelpContent(content);
                setShowHelpPane(true);
              }
            }}
          />
        </p>
        <Button variant="primary" onClick={handleCreate}>+ Create Survey</Button>
      </div>

      {loading ? (
        <div className="d-flex justify-content-center"><Spinner animation="border" /></div>
      ) : (
        <Card>
          <Card.Body>
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Survey #</th>
                  <th>Date</th>
                  <th>Material</th>
                  <th>Surveyor</th>
                  <th>Vendor</th>
                  <th>Vendor Site</th>
                  <th>Est. Quantity</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {surveys.map(s => (
                  <tr key={s.id}>
                    <td>{s.surveyNumber}</td>
                    <td>{formatDate(s.surveyDate)}</td>
                    <td>{s.item?.name || '-'}</td>
                    <td>{s.surveyor ? userLabel(s.surveyor) : '-'}</td>
                    <td>{s.vendor?.name || '-'}</td>
                    <td>{formatSiteLocation(s)}</td>
                    <td>
                      {s.estimatedQuantity != null
                        ? `${s.estimatedQuantity} ${s.unitOfMeasurement?.symbol || s.unitOfMeasurement?.name || ''}`
                        : '-'}
                      {s.price != null && (
                        <div className="text-muted small">
                          {priceCurrencyLabel(s)} {s.price}
                        </div>
                      )}
                    </td>
                    <td>{statusBadge(s.status)}</td>
                    <td className="text-nowrap">
                      <span title="View & Print" style={{ display: 'inline-block' }}>
                        <Button
                          size="sm"
                          variant="outline-info"
                          className="me-2"
                          onClick={() => handleViewPrint(s)}
                        >
                          {ICON_PRINT}
                        </Button>
                      </span>
                      <span title="View Transactions" style={{ display: 'inline-block' }}>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          className="me-2"
                          onClick={() => handleViewTransactions(s)}
                        >
                          {ICON_TRANSACTIONS}
                        </Button>
                      </span>
                      {(s.status === 'NEW' || s.status === 'IN_PROGRESS') && (
                        <>
                          <span title="Edit" style={{ display: 'inline-block' }}>
                            <Button size="sm" variant="outline-primary" className="me-2" onClick={() => handleEdit(s)}>
                              {ICON_EDIT}
                            </Button>
                          </span>
                          <span title="Mark Completed" style={{ display: 'inline-block' }}>
                            <Button size="sm" variant="outline-success" className="me-2" onClick={() => openStatusAction(s, 'complete')}>
                              {ICON_COMPLETE}
                            </Button>
                          </span>
                          {s.status === 'NEW' && (
                            <span title="Cancel" style={{ display: 'inline-block' }}>
                              <Button size="sm" variant="outline-warning" className="me-2" onClick={() => openStatusAction(s, 'cancel')}>
                                {ICON_CANCEL}
                              </Button>
                            </span>
                          )}
                          {s.status === 'IN_PROGRESS' && (
                            <span title="Cannot cancel — GRN exists against this survey" style={{ display: 'inline-block' }}>
                              <Button size="sm" variant="outline-warning" className="me-2" disabled>
                                {ICON_CANCEL}
                              </Button>
                            </span>
                          )}
                          {s.status === 'NEW' && (
                            <span title="Delete" style={{ display: 'inline-block' }}>
                              <Button size="sm" variant="outline-danger" onClick={() => handleDelete(s.id)}>
                                {ICON_DELETE}
                              </Button>
                            </span>
                          )}
                        </>
                      )}
                      {(s.status === 'COMPLETED' || s.status === 'CANCELLED') && s.statusComments && (
                        <span className="text-muted small" title={s.statusComments}>
                          {s.statusComments.length > 40 ? `${s.statusComments.slice(0, 40)}…` : s.statusComments}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
                {surveys.length === 0 && (
                  <tr>
                    <td colSpan="9" className="text-center text-muted">
                      No surveys yet. Create a procurement survey (field slip) to get started.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      <Modal show={showModal} onHide={() => { setShowModal(false); resetForm(); }} size="xl">
        <Form onSubmit={handleSave}>
          <Modal.Header closeButton>
            <Modal.Title>{editing ? 'Edit Procurement Survey' : 'Create Procurement Survey'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Survey Number</Form.Label>
                  <Form.Control
                    placeholder="Auto-generated if empty"
                    value={formData.surveyNumber}
                    onChange={(e) => setFormData({ ...formData, surveyNumber: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Survey Date *</Form.Label>
                  <DateInput
                   
                    required
                    value={formData.surveyDate}
                    onChange={(e) => setFormData({ ...formData, surveyDate: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Surveyor / Field Officer</Form.Label>
                  <Form.Select
                    value={formData.surveyorId}
                    onChange={(e) => setFormData({ ...formData, surveyorId: e.target.value })}
                  >
                    <option value="">— Select —</option>
                    {users.map(u => (
                      <option key={u.id} value={u.id}>{userLabel(u)}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Supply Vendor (Farmer) *</Form.Label>
                  <SearchableSelect
                    required
                    options={vendorOptions}
                    value={formData.vendorId}
                    onChange={(vendorId) => setFormData({ ...formData, vendorId })}
                    placeholder="Type to search vendors…"
                    emptyLabel="No vendors match your search"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>H&amp;T Vendor</Form.Label>
                  <SearchableSelect
                    options={vendorOptions}
                    value={formData.htVendorId}
                    onChange={(htVendorId) => setFormData({ ...formData, htVendorId })}
                    placeholder="Type to search vendors…"
                    emptyLabel="No vendors match your search"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Material *</Form.Label>
              <SearchableSelect
                required
                options={materialOptions}
                value={formData.itemId}
                onChange={handleMaterialChange}
                placeholder="Type to search materials…"
                emptyLabel="No materials match — add them in Org Admin → Materials"
              />
              {materials.length === 0 && (
                <Form.Text className="text-muted">
                  No organization materials yet. Add materials under Org Admin → Materials.
                </Form.Text>
              )}
            </Form.Group>

            {loadingMaterialCatalog && formData.itemId && (
              <div className="d-flex align-items-center gap-2 mb-3 text-muted">
                <Spinner animation="border" size="sm" />
                <span>Loading material details…</span>
              </div>
            )}

            {!loadingMaterialCatalog && materialCatalogDetails?.item && (
              <Card className="mb-3 border-info">
                <Card.Header className="py-2 bg-body-secondary">
                  <strong>Selected material</strong>
                  <span className="text-muted small ms-2">(from Material Master)</span>
                </Card.Header>
                <Card.Body className="py-3">
                  <Row>
                    <Col md={6}>
                      <p className="mb-2">
                        <strong>Name:</strong> {materialCatalogDetails.item.name}
                      </p>
                      <p className="mb-2">
                        <strong>Category:</strong>{' '}
                        {materialCatalogDetails.item.itemType?.name || '—'}
                      </p>
                      <p className="mb-0">
                        <strong>Master SKU:</strong> {materialCatalogDetails.item.sku || '—'}
                      </p>
                    </Col>
                    <Col md={6}>
                      <p className="mb-1"><strong>Description</strong></p>
                      <p className="text-muted mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                        {materialCatalogDetails.item.description || '—'}
                      </p>
                    </Col>
                  </Row>
                  <hr className="my-3" />
                  <p className="mb-2"><strong>Attributes</strong></p>
                  {(!materialCatalogDetails.attributeDetails
                    || materialCatalogDetails.attributeDetails.length === 0) ? (
                    <p className="text-muted small mb-0">
                      No attributes defined for this category.
                    </p>
                  ) : (
                    <Table size="sm" bordered responsive className="mb-0">
                      <thead>
                        <tr>
                          <th>Attribute</th>
                          <th>Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {materialCatalogDetails.attributeDetails.map((attr) => (
                          <tr key={attr.code}>
                            <td>
                              {attr.name}
                              {attr.description && (
                                <div className="small text-muted">{attr.description}</div>
                              )}
                            </td>
                            <td>{attr.displayValue ?? <span className="text-muted">—</span>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Card.Body>
              </Card>
            )}

            <h6 className="mt-2 mb-3">Vendor Site (Farm field / origin)</h6>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Site Name</Form.Label>
                  <Form.Control
                    value={formData.siteName}
                    onChange={(e) => setFormData({ ...formData, siteName: e.target.value })}
                    placeholder="e.g. North Field, Plot 12"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Area (acres)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.0001"
                    value={formData.areaAcres}
                    onChange={(e) => setFormData({ ...formData, areaAcres: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Address</Form.Label>
              <Form.Control
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
            </Form.Group>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>City</Form.Label>
                  <Form.Control
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="City"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>District</Form.Label>
                  <Form.Control
                    value={formData.district}
                    onChange={(e) => setFormData({ ...formData, district: e.target.value })}
                    placeholder="District"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>State/Province</Form.Label>
                  {selectedCountryCode && selectedCountryCode !== 'OTHER' && getStatesForCountry(selectedCountryCode).length > 0 ? (
                    <Form.Select
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    >
                      <option value="">Select State/Province</option>
                      {getStatesForCountry(selectedCountryCode).map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </Form.Select>
                  ) : (
                    <Form.Control
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      placeholder="State/Province"
                    />
                  )}
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Country</Form.Label>
                  <Form.Select
                    value={selectedCountryCode}
                    onChange={(e) => {
                      const countryCode = e.target.value;
                      const countryObj = countries.find(c => c.code === countryCode);
                      setSelectedCountryCode(countryCode);
                      const nextCountry = countryCode === 'OTHER' ? formData.country : (countryObj?.name || '');
                      const siteCur = getCurrencyForCountry(countryCode === 'OTHER' ? nextCountry : countryCode);
                      setFormData({
                        ...formData,
                        country: nextCountry,
                        state: '',
                        priceCurrency: formData.priceCurrency || siteCur || orgCurrency || ''
                      });
                    }}
                  >
                    <option value="">Select Country</option>
                    {countries.map(country => (
                      <option key={country.code} value={country.code}>{country.name}</option>
                    ))}
                    <option value="OTHER">Other</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Postal Code</Form.Label>
                  <Form.Control
                    value={formData.postalCode}
                    onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                    placeholder="Postal Code"
                  />
                </Form.Group>
              </Col>
            </Row>
            {selectedCountryCode === 'OTHER' && (
              <Form.Group className="mb-3">
                <Form.Label>Country Name</Form.Label>
                <Form.Control
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                />
              </Form.Group>
            )}

            <h6 className="mt-3 mb-3">Quantity &amp; Price</h6>
            {currencyMismatch && (
              <Alert variant="warning" className="py-2">
                Organization currency ({orgCurrency || 'not set'}) does not match the currency expected for
                the organization/site country. Please confirm the currency for this price/rate.
              </Alert>
            )}
            <Row>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Estimated Quantity</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.001"
                    value={formData.estimatedQuantity}
                    onChange={(e) => setFormData({ ...formData, estimatedQuantity: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>UOM</Form.Label>
                  <Form.Select
                    value={formData.unitOfMeasurementId}
                    onChange={(e) => setFormData({ ...formData, unitOfMeasurementId: e.target.value })}
                  >
                    <option value="">Select UOM</option>
                    {uoms.map(u => (
                      <option key={u.id} value={u.id}>{u.name}{u.symbol ? ` (${u.symbol})` : ''}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Binding Deductions (%)</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={formData.bindingDeductions}
                      onChange={(e) => setFormData({ ...formData, bindingDeductions: e.target.value })}
                    />
                    <InputGroup.Text>%</InputGroup.Text>
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Price (rate / UOM)</Form.Label>
                  {currencyMismatch ? (
                    <InputGroup>
                      <Form.Select
                        style={{ maxWidth: '110px' }}
                        value={formData.priceCurrency}
                        onChange={(e) => setFormData({ ...formData, priceCurrency: e.target.value })}
                        required
                      >
                        <option value="">Currency</option>
                        {commonCurrencyCodes.map(c => (
                          <option key={c.code} value={c.code}>{c.code}</option>
                        ))}
                      </Form.Select>
                      <Form.Control
                        type="number"
                        step="0.0001"
                        value={formData.price}
                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      />
                    </InputGroup>
                  ) : (
                    <InputGroup>
                      {displayCurrency && <InputGroup.Text>{displayCurrency}</InputGroup.Text>}
                      <Form.Control
                        type="number"
                        step="0.0001"
                        value={formData.price}
                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      />
                    </InputGroup>
                  )}
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Remarks</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={formData.remarks}
                onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => { setShowModal(false); resetForm(); }}>Cancel</Button>
            <Button variant="primary" type="submit">{editing ? 'Update' : 'Create'}</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={!!statusAction} onHide={closeStatusAction} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {statusAction?.type === 'cancel' ? 'Cancel Survey' : 'Complete Survey'}
            {statusAction?.surveyNumber ? ` — ${statusAction.surveyNumber}` : ''}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small mb-3">
            {statusAction?.type === 'cancel'
              ? 'Cancel only if no GRN has been created against this survey. Comments are required.'
              : 'Mark completed when field weighing is finished. You can still create GRNs against a completed survey; cancel the survey if it should no longer be used.'}
          </p>
          <Form.Group>
            <Form.Label>
              Comments{statusAction?.type === 'cancel' ? ' *' : ' (optional)'}
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={statusComments}
              onChange={(e) => setStatusComments(e.target.value)}
              placeholder={
                statusAction?.type === 'cancel'
                  ? 'Reason for cancellation (required)'
                  : 'Optional notes about completion'
              }
              required={statusAction?.type === 'cancel'}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeStatusAction} disabled={statusActionSaving}>
            Back
          </Button>
          <Button
            variant={statusAction?.type === 'cancel' ? 'warning' : 'success'}
            onClick={confirmStatusAction}
            disabled={statusActionSaving}
          >
            {statusActionSaving
              ? 'Saving…'
              : statusAction?.type === 'cancel'
                ? 'Confirm Cancel'
                : 'Mark Completed'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showTransactionsModal}
        onHide={closeTransactionsModal}
        size="lg"
        className="print-modal"
      >
        <Modal.Header className="print-header">
          <Modal.Title>
            Transactions — {selectedSurveyForTransactions?.surveyNumber || 'Procurement Survey'}
          </Modal.Title>
          <div className="print-actions">
            <Button
              variant="primary"
              className="me-2"
              onClick={handlePrint}
              disabled={loadingSurveyTransactions}
            >
              Print
            </Button>
            <Button variant="secondary" onClick={closeTransactionsModal}>
              Close
            </Button>
          </div>
        </Modal.Header>
        <Modal.Body className="print-body">
          {loadingSurveyTransactions ? (
            <div className="d-flex justify-content-center py-4">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          ) : (
            <OrgPrintChrome>
              <div className="print-content">
                <div className="text-center mb-3">
                  <h2>PROCUREMENT SURVEY TRANSACTIONS</h2>
                  <p className="text-muted mb-0">
                    Survey Number: <strong>{selectedSurveyForTransactions?.surveyNumber || '-'}</strong>
                  </p>
                </div>

                <h6 className="mb-2">Weigh Bridge Receipts</h6>
                {surveyTransactions.weighBridgeReceipts && surveyTransactions.weighBridgeReceipts.length > 0 ? (
                  <Table size="sm" striped bordered className="mb-4">
                    <thead>
                      <tr>
                        <th>RST #</th>
                        <th>Date</th>
                        <th>Vehicle</th>
                        <th>Material</th>
                        <th>Net Weight</th>
                        <th>Vendor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {surveyTransactions.weighBridgeReceipts.map((w, idx) => (
                        <tr key={w.id != null ? `wbr-${w.id}` : `wbr-${idx}`}>
                          <td>{w.rstNumber || '-'}</td>
                          <td>
                            {formatDate(w.receiptDate) || '-'}
                            {w.receiptTime ? ` ${String(w.receiptTime).slice(0, 5)}` : ''}
                          </td>
                          <td>{w.vehicleNo || '-'}</td>
                          <td>{w.materialName || '-'}</td>
                          <td>{formatWeight(w.netWeight)}</td>
                          <td>{w.vendorName || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <p className="text-muted mb-4">No weigh bridge receipts against this survey yet.</p>
                )}

                <h6 className="mb-2">Deliveries (GRNs)</h6>
                {surveyTransactions.deliveries && surveyTransactions.deliveries.length > 0 ? (
                  <div className="mb-2">
                    {surveyTransactions.deliveries.map((d, idx) => (
                      <div key={d.id != null ? d.id : `d-${idx}`} className="mb-3">
                        <p className="mb-1">
                          <strong>{d.grnNumber || '-'}</strong>
                          {' — '}{formatDate(d.grnDate) || '-'}
                          {' — '}{d.warehouseName || d.retailStoreName || d.productionUnitName || '-'}
                          {d.status && (
                            <>
                              {' — '}
                              <Badge bg={d.status === 'RECEIVED' || d.status === 'COMPLETED' ? 'success' : 'secondary'}>
                                {d.status}
                              </Badge>
                            </>
                          )}
                        </p>
                        {d.lineItems && d.lineItems.length > 0 ? (
                          <Table size="sm" striped bordered className="mb-0">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>Type</th>
                                <th>Item / Service</th>
                                <th>Vendor</th>
                                <th>Received Qty</th>
                                <th>UOM</th>
                                <th>Unit Price</th>
                                <th>Line Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {d.lineItems.map((li, liIdx) => {
                                const name = li.itemName || li.serviceName || '-';
                                const qty = li.receivedQuantity != null
                                  ? parseFloat(li.receivedQuantity).toLocaleString('en-US', {
                                      minimumFractionDigits: 3,
                                      maximumFractionDigits: 3
                                    })
                                  : '-';
                                return (
                                  <tr key={li.id != null ? li.id : `li-${liIdx}`}>
                                    <td>{liIdx + 1}</td>
                                    <td>{li.lineType || (li.serviceName ? 'SERVICE' : 'MATERIAL')}</td>
                                    <td>{name}</td>
                                    <td>{li.vendorName || '-'}</td>
                                    <td>{qty}</td>
                                    <td>{li.uomSymbol || '-'}</td>
                                    <td>{li.unitPrice != null ? formatCurrency(Number(li.unitPrice)) : '-'}</td>
                                    <td>{li.lineTotal != null ? formatCurrency(Number(li.lineTotal)) : '-'}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </Table>
                        ) : (
                          <p className="text-muted small mb-0">No line items on this GRN.</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted mb-0">No deliveries (GRNs) against this survey yet.</p>
                )}
              </div>
            </OrgPrintChrome>
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={showPrintModal}
        onHide={() => { setShowPrintModal(false); setPrintData(null); }}
        size="xl"
        className="print-modal"
      >
        <Modal.Header className="print-header">
          <Modal.Title>Procurement Survey</Modal.Title>
          <div className="print-actions">
            <Button variant="primary" onClick={handlePrint} className="me-2">
              Print
            </Button>
            <Button variant="secondary" onClick={() => { setShowPrintModal(false); setPrintData(null); }}>
              Close
            </Button>
          </div>
        </Modal.Header>
        <Modal.Body className="print-body">
          <OrgPrintChrome>
            {printData && (
              <div className="print-content">
                <div className="text-center mb-4">
                  <h2>PROCUREMENT SURVEY</h2>
                  <p className="text-muted">Survey Number: <strong>{printData.surveyNumber}</strong></p>
                </div>
                <Row className="mb-3">
                  <Col md={6}>
                    <p><strong>Survey Date:</strong> {formatDate(printData.surveyDate) || '-'}</p>
                    <p><strong>Surveyor:</strong> {printData.surveyor ? userLabel(printData.surveyor) : '-'}</p>
                    <p><strong>Material:</strong> {printData.item?.name || '-'}</p>
                    <p><strong>Status:</strong> {printData.status || '-'}</p>
                  </Col>
                  <Col md={6}>
                    <p><strong>Supply Vendor:</strong> {printData.vendor?.name || '-'}</p>
                    <p><strong>H&amp;T Vendor:</strong> {printData.htVendor?.name || '-'}</p>
                    {printData.statusComments && (
                      <p><strong>Status Comments:</strong> {printData.statusComments}</p>
                    )}
                  </Col>
                </Row>

                <h5 className="mt-3 mb-2">Vendor Site</h5>
                <Row className="mb-3">
                  <Col md={12}>
                    {formatSiteAddressBlock(printData).length > 0 ? (
                      formatSiteAddressBlock(printData).map((line, idx) => (
                        <div key={idx}>{line}</div>
                      ))
                    ) : (
                      <p className="text-muted mb-0">-</p>
                    )}
                  </Col>
                </Row>

                <h5 className="mt-3 mb-2">Estimate</h5>
                <Table striped bordered className="mt-2">
                  <thead>
                    <tr>
                      <th>Area (acres)</th>
                      <th>Estimated Quantity</th>
                      <th>UOM</th>
                      <th>Binding Deductions %</th>
                      <th>Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{printData.areaAcres != null ? printData.areaAcres : '-'}</td>
                      <td>
                        {printData.estimatedQuantity != null
                          ? parseFloat(printData.estimatedQuantity).toLocaleString('en-US', {
                              minimumFractionDigits: 3,
                              maximumFractionDigits: 3
                            })
                          : '-'}
                      </td>
                      <td>{printData.unitOfMeasurement?.symbol || printData.unitOfMeasurement?.name || '-'}</td>
                      <td>{printData.bindingDeductions != null ? printData.bindingDeductions : '-'}</td>
                      <td>
                        {printData.price != null
                          ? `${priceCurrencyLabel(printData)} ${parseFloat(printData.price).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 4
                            })}`
                          : '-'}
                      </td>
                    </tr>
                  </tbody>
                </Table>

                {formatSurveyAttributeEntries(printData).length > 0 && (
                  <>
                    <h5 className="mt-4 mb-2">Additional Attributes</h5>
                    <Table striped bordered size="sm">
                      <thead>
                        <tr>
                          <th>Attribute</th>
                          <th>Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {formatSurveyAttributeEntries(printData).map((entry) => (
                          <tr key={entry.key}>
                            <td>{entry.label}</td>
                            <td>{entry.value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </>
                )}

                {printData.remarks && (
                  <div className="mt-3">
                    <p><strong>Remarks:</strong> {printData.remarks}</p>
                  </div>
                )}
              </div>
            )}
          </OrgPrintChrome>
        </Modal.Body>
      </Modal>
    </>
  );
}

export default ProcurementSurveysTab;
